/**
 * O estado do painel ao lado do exame e a saída mostrada no centro da mesa única (E201.F2; E202.F1,
 * GUS-145, ADR-0145 §5).
 *
 * - O painel tem duas abas: "Documentos" (o original, E201.F2) e "Prévia (PDF)" (o arquivo que a
 *   preparação guardou, a prévia definitiva). "Ver na fonte" abre a aba dos documentos no arquivo e na
 *   página citados; o botão do cabeçalho abre a aba dele, ou fecha o painel se ela já está aberta.
 * - A saída do documento no centro (o exame ou o relatório de risco) fica no endereço (`?saida=`).
 */
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SaidaDaEdicao } from '@/lib/correcaoDoRascunho';
import type { AlvoNoPainel } from '@/lib/painelDoDocumento';

import type { DocumentoAberto } from './ControlesDoDocumento';

export type AbaDoPainel = 'documentos' | 'previa';

export interface PainelDaMesa {
  /** `null`: o painel fechado. */
  aba: AbaDoPainel | null;
  documento: DocumentoAberto;
  setDocumento: (documento: DocumentoAberto) => void;
  alternar: (aba: AbaDoPainel) => void;
  /** E205.F2: abre o painel na aba pedida, sem fechar se ela já está aberta ("Ver prévia (PDF)"). */
  mostrar: (aba: AbaDoPainel) => void;
  fechar: () => void;
  contexto: { abrir: (alvo: AlvoNoPainel) => void };
}

export function usePainelDaMesa(): PainelDaMesa {
  const [aba, setAba] = useState<AbaDoPainel | null>(null);
  const [documento, setDocumento] = useState<DocumentoAberto>({ documentoId: null, pagina: 1 });
  const abrir = useCallback((alvo: AlvoNoPainel) => {
    setDocumento({ documentoId: alvo.documentoId, pagina: alvo.pagina });
    setAba('documentos');
  }, []);
  const alternar = useCallback(
    (pedida: AbaDoPainel) => setAba((atual) => (atual === pedida ? null : pedida)),
    [],
  );
  const mostrar = useCallback((pedida: AbaDoPainel) => setAba(pedida), []);
  const fechar = useCallback(() => setAba(null), []);
  const contexto = useMemo(() => ({ abrir }), [abrir]);
  return { aba, documento, setDocumento, alternar, mostrar, fechar, contexto };
}

/** A saída mostrada no centro, pelo endereço: o exame, salvo `?saida=riscos`. */
export function useSaidaDaMesa(): [SaidaDaEdicao, (saida: SaidaDaEdicao) => void] {
  const [busca, setBusca] = useSearchParams();
  const saida: SaidaDaEdicao = busca.get('saida') === 'riscos' ? 'riscos' : 'exame';
  const trocar = useCallback(
    (nova: SaidaDaEdicao) =>
      setBusca(
        (anterior) => {
          const proxima = new URLSearchParams(anterior);
          if (nova === 'exame') proxima.delete('saida');
          else proxima.set('saida', nova);
          return proxima;
        },
        { replace: true },
      ),
    [setBusca],
  );
  return [saida, trocar];
}
